/**
 * Icons Page - Visual showcase of all event type icons
 * Route: /icons
 * Displays all diff event type icons grouped by category
 * Aligned with reports classification (EVENT_TYPE_CATEGORIES)
 * Designed for social media assets
 */

import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  EyeSlashIcon,
  TagIcon,
  Cog6ToothIcon,
  Square3Stack3DIcon,
  ChartBarIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  GiftIcon,
  CubeIcon,
  BoltIcon,
} from '@heroicons/react/24/outline';
import {
  ArrowTrendingUpIcon as ArrowTrendingUpIconSolid,
  ArrowTrendingDownIcon as ArrowTrendingDownIconSolid,
  EyeIcon as EyeIconSolid,
  EyeSlashIcon as EyeSlashIconSolid,
  TagIcon as TagIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
  Square3Stack3DIcon as Square3Stack3DIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  PlusCircleIcon as PlusCircleIconSolid,
  MinusCircleIcon as MinusCircleIconSolid,
  ArrowUpIcon as ArrowUpIconSolid,
  ArrowDownIcon as ArrowDownIconSolid,
  ClockIcon as ClockIconSolid,
  GiftIcon as GiftIconSolid,
  CubeIcon as CubeIconSolid,
  BoltIcon as BoltIconSolid,
} from '@heroicons/react/24/solid';
import {
  TrendingUp,
  TrendingDown,
  Eye,
  EyeOff,
  Tag,
  Settings,
  Layers,
  BarChart3,
  Plus,
  Minus,
  Clock,
  Gift,
  Package,
  Zap,
  Gauge,
} from 'lucide-react';
import {
  IconTrendingUp,
  IconTrendingDown,
  IconEye,
  IconEyeOff,
  IconTag,
  IconSettings,
  IconStack,
  IconChartBar,
  IconPlus,
  IconMinus,
  IconClock,
  IconGift,
  IconPackage,
  IconBolt,
  IconArrowUp,
  IconArrowDown,
  IconCirclePlus,
  IconCircleMinus,
} from '@tabler/icons-react';
import {
  TrendUp,
  TrendDown,
  Eye as EyePhosphor,
  EyeSlash,
  Tag as TagPhosphor,
  Gear,
  Stack as StackPhosphor,
  ChartBar as ChartBarPhosphor,
  Plus as PlusPhosphor,
  Minus as MinusPhosphor,
  Clock as ClockPhosphor,
  Gift as GiftPhosphor,
  Package as PackagePhosphor,
  Lightning,
  ArrowUp as ArrowUpPhosphor,
  ArrowDown as ArrowDownPhosphor,
  PlusCircle,
  MinusCircle,
} from 'phosphor-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useState, useRef } from 'react';
import JSZip from 'jszip';
import { toPng } from 'html-to-image';
import { saveAs } from 'file-saver';
import { Download } from 'lucide-react';

interface IconConfig {
  name: string;
  color: string;
  bgColor: string;
  description: string;
  eventType: string; // Actual event_type from database
}

interface IconCategory {
  title: string;
  subtitle: string;
  icons: IconConfig[];
  accentColor: string;
  count: number;
}

type IconType = 'heroicons-outline' | 'heroicons-solid' | 'lucide' | 'tabler' | 'phosphor';

const Icons = () => {
  const [viewMode, setViewMode] = useState<'detailed' | 'condensed'>('detailed');
  const [iconType, setIconType] = useState<IconType>('heroicons-outline');
  const [isDownloading, setIsDownloading] = useState(false);
  const iconRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Icon mapping function - returns the appropriate icon based on event type and icon library
  const getIconForType = (eventType: string, type: IconType): React.ElementType => {
    const iconMaps = {
      'heroicons-outline': {
        price_increased: ArrowTrendingUpIcon,
        price_decreased: ArrowTrendingDownIcon,
        price_hidden: EyeSlashIcon,
        price_revealed: EyeIcon,
        discount_added: TagIcon,
        discount_removed: TagIcon,
        discount_changed: TagIcon,
        pricing_restructured: Cog6ToothIcon,
        plan_added: Square3Stack3DIcon,
        plan_removed: Square3Stack3DIcon,
        plan_renamed: Square3Stack3DIcon,
        pricing_metric_changed: ChartBarIcon,
        threshold_added: PlusCircleIcon,
        threshold_removed: MinusCircleIcon,
        capacity_increased: ArrowUpIcon,
        capacity_decreased: ArrowDownIcon,
        trial_changed: ClockIcon,
        freemium_changed: GiftIcon,
        addon_added: CubeIcon,
        addon_removed: CubeIcon,
        addon_changed: CubeIcon,
        feature_added: BoltIcon,
        feature_removed: BoltIcon,
        feature_changed: BoltIcon,
        positioning_changed: Cog6ToothIcon,
      },
      'heroicons-solid': {
        price_increased: ArrowTrendingUpIconSolid,
        price_decreased: ArrowTrendingDownIconSolid,
        price_hidden: EyeSlashIconSolid,
        price_revealed: EyeIconSolid,
        discount_added: TagIconSolid,
        discount_removed: TagIconSolid,
        discount_changed: TagIconSolid,
        pricing_restructured: Cog6ToothIconSolid,
        plan_added: Square3Stack3DIconSolid,
        plan_removed: Square3Stack3DIconSolid,
        plan_renamed: Square3Stack3DIconSolid,
        pricing_metric_changed: ChartBarIconSolid,
        threshold_added: PlusCircleIconSolid,
        threshold_removed: MinusCircleIconSolid,
        capacity_increased: ArrowUpIconSolid,
        capacity_decreased: ArrowDownIconSolid,
        trial_changed: ClockIconSolid,
        freemium_changed: GiftIconSolid,
        addon_added: CubeIconSolid,
        addon_removed: CubeIconSolid,
        addon_changed: CubeIconSolid,
        feature_added: BoltIconSolid,
        feature_removed: BoltIconSolid,
        feature_changed: BoltIconSolid,
        positioning_changed: Cog6ToothIconSolid,
      },
      'lucide': {
        price_increased: TrendingUp,
        price_decreased: TrendingDown,
        price_hidden: EyeOff,
        price_revealed: Eye,
        discount_added: Tag,
        discount_removed: Tag,
        discount_changed: Tag,
        pricing_restructured: Settings,
        plan_added: Layers,
        plan_removed: Layers,
        plan_renamed: Layers,
        pricing_metric_changed: Gauge,
        threshold_added: Plus,
        threshold_removed: Minus,
        capacity_increased: TrendingUp,
        capacity_decreased: TrendingDown,
        trial_changed: Clock,
        freemium_changed: Gift,
        addon_added: Package,
        addon_removed: Package,
        addon_changed: Package,
        feature_added: Zap,
        feature_removed: Zap,
        feature_changed: Zap,
        positioning_changed: Settings,
      },
      'tabler': {
        price_increased: IconTrendingUp,
        price_decreased: IconTrendingDown,
        price_hidden: IconEyeOff,
        price_revealed: IconEye,
        discount_added: IconTag,
        discount_removed: IconTag,
        discount_changed: IconTag,
        pricing_restructured: IconSettings,
        plan_added: IconStack,
        plan_removed: IconStack,
        plan_renamed: IconStack,
        pricing_metric_changed: IconChartBar,
        threshold_added: IconCirclePlus,
        threshold_removed: IconCircleMinus,
        capacity_increased: IconArrowUp,
        capacity_decreased: IconArrowDown,
        trial_changed: IconClock,
        freemium_changed: IconGift,
        addon_added: IconPackage,
        addon_removed: IconPackage,
        addon_changed: IconPackage,
        feature_added: IconBolt,
        feature_removed: IconBolt,
        feature_changed: IconBolt,
        positioning_changed: IconSettings,
      },
      'phosphor': {
        price_increased: TrendUp,
        price_decreased: TrendDown,
        price_hidden: EyeSlash,
        price_revealed: EyePhosphor,
        discount_added: TagPhosphor,
        discount_removed: TagPhosphor,
        discount_changed: TagPhosphor,
        pricing_restructured: Gear,
        plan_added: StackPhosphor,
        plan_removed: StackPhosphor,
        plan_renamed: StackPhosphor,
        pricing_metric_changed: ChartBarPhosphor,
        threshold_added: PlusCircle,
        threshold_removed: MinusCircle,
        capacity_increased: ArrowUpPhosphor,
        capacity_decreased: ArrowDownPhosphor,
        trial_changed: ClockPhosphor,
        freemium_changed: GiftPhosphor,
        addon_added: PackagePhosphor,
        addon_removed: PackagePhosphor,
        addon_changed: PackagePhosphor,
        feature_added: Lightning,
        feature_removed: Lightning,
        feature_changed: Lightning,
        positioning_changed: Gear,
      },
    };

    return iconMaps[type][eventType as keyof typeof iconMaps[typeof type]];
  };

  const downloadAllIcons = async () => {
    setIsDownloading(true);
    const zip = new JSZip();

    try {
      // Get icon library name for folder structure
      const libraryName = iconType.replace('-', '_');

      for (const category of categories) {
        for (const iconConfig of category.icons) {
          const iconElement = iconRefs.current[iconConfig.eventType];

          if (iconElement) {
            try {
              // Convert icon to PNG
              const dataUrl = await toPng(iconElement, {
                quality: 1,
                pixelRatio: 3, // Higher resolution
                backgroundColor: '#ffffff',
              });

              // Convert data URL to blob
              const response = await fetch(dataUrl);
              const blob = await response.blob();

              // Add to zip with organized folder structure
              const fileName = `${category.title.toLowerCase().replace(/\s+/g, '_')}/${iconConfig.eventType}.png`;
              zip.file(fileName, blob);
            } catch (error) {
              console.error(`Failed to capture ${iconConfig.name}:`, error);
            }
          }
        }
      }

      // Generate zip and download
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `pricing-icons-${libraryName}.zip`);
    } catch (error) {
      console.error('Failed to create zip file:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const categories: IconCategory[] = [
    {
      title: 'Pricing Events',
      subtitle: 'Price changes, discounts, and restructuring',
      accentColor: 'from-[#d8e364] to-[#aebb36]', // Lime to Dark Lime
      count: 8,
      icons: [
        {
          name: 'Price Increased',
          eventType: 'price_increased',
          color: 'text-[#aebb36]', // Dark Lime
          bgColor: 'bg-white',
          description: 'Upward price movement',
        },
        {
          name: 'Price Decreased',
          eventType: 'price_decreased',
          color: 'text-red-600',
          bgColor: 'bg-white',
          description: 'Downward price movement',
        },
        {
          name: 'Price Hidden',
          eventType: 'price_hidden',
          color: 'text-[#131c3b]', // Navi
          bgColor: 'bg-white',
          description: 'Price removed from display',
        },
        {
          name: 'Price Revealed',
          eventType: 'price_revealed',
          color: 'text-[#3e5dc2]', // Blue
          bgColor: 'bg-white',
          description: 'Price now publicly visible',
        },
        {
          name: 'Discount Added',
          eventType: 'discount_added',
          color: 'text-[#aebb36]', // Dark Lime (same as Price Increased)
          bgColor: 'bg-white',
          description: 'New discount available',
        },
        {
          name: 'Discount Removed',
          eventType: 'discount_removed',
          color: 'text-red-600',
          bgColor: 'bg-white',
          description: 'Discount no longer offered',
        },
        {
          name: 'Discount Changed',
          eventType: 'discount_changed',
          color: 'text-[#d4a429]', // Amber/Gold (for changed events)
          bgColor: 'bg-white',
          description: 'Discount terms modified',
        },
        {
          name: 'Pricing Restructured',
          eventType: 'pricing_restructured',
          color: 'text-[#9fadf4]', // Purple
          bgColor: 'bg-white',
          description: 'Major pricing overhaul',
        },
      ],
    },
    {
      title: 'Packaging Events',
      subtitle: 'Plans, add-ons, thresholds, and capacity',
      accentColor: 'from-[#3e5dc2] to-[#9fadf4]', // Blue to Purple
      count: 13,
      icons: [
        {
          name: 'Plan Added',
          eventType: 'plan_added',
          color: 'text-[#3e5dc2]', // Blue
          bgColor: 'bg-white',
          description: 'New pricing tier introduced',
        },
        {
          name: 'Plan Removed',
          eventType: 'plan_removed',
          color: 'text-red-600',
          bgColor: 'bg-white',
          description: 'Pricing tier discontinued',
        },
        {
          name: 'Plan Renamed',
          eventType: 'plan_renamed',
          color: 'text-[#d4a429]', // Amber/Gold (for changed events)
          bgColor: 'bg-white',
          description: 'Plan name updated',
        },
        {
          name: 'Pricing Metric Changed',
          eventType: 'pricing_metric_changed',
          color: 'text-[#d4a429]', // Amber/Gold (for changed events)
          bgColor: 'bg-white',
          description: 'Billing metric modified',
        },
        {
          name: 'Threshold Added',
          eventType: 'threshold_added',
          color: 'text-[#aebb36]', // Dark Lime
          bgColor: 'bg-white',
          description: 'New usage threshold introduced',
        },
        {
          name: 'Threshold Removed',
          eventType: 'threshold_removed',
          color: 'text-red-600',
          bgColor: 'bg-white',
          description: 'Usage threshold eliminated',
        },
        {
          name: 'Capacity Increased',
          eventType: 'capacity_increased',
          color: 'text-[#aebb36]', // Dark Lime
          bgColor: 'bg-white',
          description: 'Higher usage limits',
        },
        {
          name: 'Capacity Decreased',
          eventType: 'capacity_decreased',
          color: 'text-red-600',
          bgColor: 'bg-white',
          description: 'Lower usage limits',
        },
        {
          name: 'Trial Changed',
          eventType: 'trial_changed',
          color: 'text-[#d4a429]', // Amber/Gold (for changed events)
          bgColor: 'bg-white',
          description: 'Trial period adjusted',
        },
        {
          name: 'Freemium Changed',
          eventType: 'freemium_changed',
          color: 'text-[#d4a429]', // Amber/Gold (for changed events)
          bgColor: 'bg-white',
          description: 'Free tier modified',
        },
        {
          name: 'Add-on Added',
          eventType: 'addon_added',
          color: 'text-[#9fadf4]', // Purple
          bgColor: 'bg-white',
          description: 'New optional add-on',
        },
        {
          name: 'Add-on Removed',
          eventType: 'addon_removed',
          color: 'text-red-600',
          bgColor: 'bg-white',
          description: 'Add-on discontinued',
        },
        {
          name: 'Add-on Changed',
          eventType: 'addon_changed',
          color: 'text-[#d4a429]', // Amber/Gold (for changed events)
          bgColor: 'bg-white',
          description: 'Add-on terms updated',
        },
      ],
    },
    {
      title: 'Product Events',
      subtitle: 'Feature additions, removals, and changes',
      accentColor: 'from-[#9fadf4] to-[#3e5dc2]', // Purple to Blue
      count: 4,
      icons: [
        {
          name: 'Feature Added',
          eventType: 'feature_added',
          color: 'text-[#aebb36]', // Dark Lime
          bgColor: 'bg-white',
          description: 'New capability included',
        },
        {
          name: 'Feature Removed',
          eventType: 'feature_removed',
          color: 'text-red-600',
          bgColor: 'bg-white',
          description: 'Feature no longer available',
        },
        {
          name: 'Feature Changed',
          eventType: 'feature_changed',
          color: 'text-[#d4a429]', // Amber/Gold (for changed events)
          bgColor: 'bg-white',
          description: 'Feature modified or updated',
        },
        {
          name: 'Positioning Changed',
          eventType: 'positioning_changed',
          color: 'text-[#d4a429]', // Amber/Gold (same as other "changed" events)
          bgColor: 'bg-white',
          description: 'Positioning or messaging updated',
        },
      ],
    },
  ];

  const totalEvents = categories.reduce((sum, cat) => sum + cat.count, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f7f7f7] via-white to-[#faffcc]/30">
      <div className="max-w-7xl mx-auto px-8 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-[#131c3b] mb-4">
            Pricing Change Icons
          </h1>
          <p className="text-xl text-[#191919]/80 max-w-2xl mx-auto mb-2">
            Visual language for tracking SaaS pricing evolution across pricing, packaging, and product dimensions
          </p>
          <p className="text-sm text-[#131c3b]/60">
            {totalEvents} event types across 3 categories
          </p>

          {/* Controls */}
          <div className="flex flex-col items-center gap-4 mt-6">
            {/* Icon Type Selector and Download */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-[#131c3b]">Icon Library:</label>
                <Select value={iconType} onValueChange={(value) => setIconType(value as IconType)}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="heroicons-outline">Heroicons Outline</SelectItem>
                    <SelectItem value="heroicons-solid">Heroicons Solid</SelectItem>
                    <SelectItem value="lucide">Lucide React</SelectItem>
                    <SelectItem value="tabler">Tabler Icons</SelectItem>
                    <SelectItem value="phosphor">Phosphor Icons</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Download Button */}
              <Button
                onClick={downloadAllIcons}
                disabled={isDownloading}
                variant="outline"
                size="sm"
                className="gap-2"
              >
                <Download className="h-4 w-4" />
                {isDownloading ? 'Downloading...' : 'Download All'}
              </Button>
            </div>

            {/* View Mode Toggle */}
            <div className="flex justify-center gap-2">
              <Button
                onClick={() => setViewMode('detailed')}
                variant={viewMode === 'detailed' ? 'default' : 'outline'}
                size="sm"
              >
                Detailed View
              </Button>
              <Button
                onClick={() => setViewMode('condensed')}
                variant={viewMode === 'condensed' ? 'default' : 'outline'}
                size="sm"
              >
                Slide View
              </Button>
            </div>
          </div>
        </div>

        {/* Detailed View */}
        {viewMode === 'detailed' && (
          <div className="space-y-16">
            {categories.map((category, categoryIndex) => (
              <div key={categoryIndex}>
                {/* Category Header */}
                <div className="mb-8">
                  <div className="flex items-center gap-4 mb-2">
                    <div className={`h-1.5 w-16 bg-gradient-to-r ${category.accentColor} rounded-full`} />
                    <h2 className="text-3xl font-bold text-[#131c3b]">
                      {category.title}
                    </h2>
                    <span className="text-sm font-medium text-[#131c3b]/70 bg-[#f7f7f7] border border-[#131c3b]/10 px-2 py-1 rounded">
                      {category.count} events
                    </span>
                  </div>
                  <p className="text-lg text-[#191919]/70 ml-20">
                    {category.subtitle}
                  </p>
                </div>

                {/* Icons Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.icons.map((iconConfig, iconIndex) => {
                    const IconComponent = getIconForType(iconConfig.eventType, iconType);

                    return (
                      <Card
                        key={iconIndex}
                        className="border-2 border-[#131c3b]/10 hover:border-[#3e5dc2]/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white"
                      >
                        <CardContent className="p-6">
                          <div className="flex items-start gap-4">
                            {/* Icon */}
                            <div
                              ref={(el) => iconRefs.current[iconConfig.eventType] = el}
                              className={`flex-shrink-0 w-14 h-14 rounded-xl ${iconConfig.bgColor} flex items-center justify-center shadow-sm border border-[#131c3b]/10`}
                            >
                              <IconComponent className={`h-8 w-8 ${iconConfig.color}`} />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-[#131c3b] text-lg mb-1">
                                {iconConfig.name}
                              </h3>
                              <p className="text-sm text-[#191919]/70 leading-relaxed">
                                {iconConfig.description}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Condensed View - Single Slide */}
        {viewMode === 'condensed' && (
          <Card className="border-2 border-[#131c3b]/10 shadow-2xl bg-white">
            <CardContent className="p-12">
              <div className="space-y-10">
                {categories.map((category, categoryIndex) => (
                  <div key={categoryIndex}>
                    {/* Category Header */}
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`h-1 w-12 bg-gradient-to-r ${category.accentColor} rounded-full`} />
                      <h3 className="text-xl font-bold text-[#131c3b]">
                        {category.title}
                      </h3>
                      <span className="text-xs font-medium text-[#131c3b]/70 bg-[#f7f7f7] border border-[#131c3b]/10 px-2 py-1 rounded">
                        {category.count}
                      </span>
                    </div>

                    {/* Compact Icons Grid */}
                    <div className="grid grid-cols-7 gap-4">
                      {category.icons.map((iconConfig, iconIndex) => {
                        const IconComponent = getIconForType(iconConfig.eventType, iconType);

                        return (
                          <div
                            key={iconIndex}
                            className="flex flex-col items-center gap-2"
                          >
                            <div
                              ref={(el) => iconRefs.current[iconConfig.eventType] = el}
                              className={`w-12 h-12 rounded-lg ${iconConfig.bgColor} flex items-center justify-center border border-[#131c3b]/10 shadow-sm`}
                            >
                              <IconComponent className={`h-7 w-7 ${iconConfig.color}`} />
                            </div>
                            <span className="text-xs font-medium text-[#131c3b] text-center leading-tight">
                              {iconConfig.name}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-20 pt-12 border-t border-[#131c3b]/10">
          <div className="text-center">
            <p className="text-[#131c3b]/70 text-sm">
              Part of the Pricing Explorer platform
            </p>
            <p className="text-[#131c3b]/50 text-xs mt-2">
              Tracking and analyzing SaaS pricing changes at scale
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Icons;
